import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Button, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import * as Sentry from '@sentry/react';
import { Switch } from '../../components/switch/Switch.tsx';
import SettingsService, { type CreditSettings, type CreditSummary } from '../../services/settings/settingsService.ts';

const settingsService = new SettingsService();

type NumericKey = Exclude<keyof CreditSettings, 'enabled'>;

const FIELDS: { key: NumericKey; unit: '%' | '€' | 'h' | 'mo' | 'uses'; step: number }[] = [
    { key: 'earnRatePercent', unit: '%', step: 0.5 },
    { key: 'tripBonusAmount', unit: '€', step: 0.5 },
    { key: 'tripBonusHours', unit: 'h', step: 1 },
    { key: 'referralAmount', unit: '€', step: 0.5 },
    { key: 'referralUnlockSpend', unit: '€', step: 5 },
    { key: 'referralMinFirstOrder', unit: '€', step: 5 },
    { key: 'expiryMonths', unit: 'mo', step: 1 },
    { key: 'referralExpiryHours', unit: 'h', step: 1 },
    { key: 'referralMaxUses', unit: 'uses', step: 1 }
];

const euro = (n: number | null | undefined) => `€${Number(n ?? 0).toFixed(2)}`;

/**
 * The Vello Credit rates, editable without a release - the 10% launch rate is
 * meant to step down from here once the goal shifts from reach to profit.
 * Above the form, this month's numbers: what has been issued against what
 * commission earned, and what is owed in total.
 */
const CreditSettingsCard = () => {
    const { t } = useTranslation();
    const notify = useNotify();

    const [form, setForm] = useState<CreditSettings | null>(null);
    const [saved, setSaved] = useState<CreditSettings | null>(null);
    const [summary, setSummary] = useState<CreditSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        Promise.all([settingsService.getCreditSettings(), settingsService.getCreditSummary()])
            .then(([settings, stats]) => {
                if (cancelled) return;
                setForm(settings);
                setSaved(settings);
                setSummary(stats);
            })
            .catch((e) => {
                Sentry.captureException(e);
                if (!cancelled) notify(t('settings.credit.loadFailed', 'Could not load the credit settings'), { type: 'error' });
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [notify, t]);

    const dirty = !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved);

    const onSave = useCallback(async () => {
        if (!form) return;
        setSaving(true);
        try {
            const stored = await settingsService.updateCreditSettings(form);
            setForm(stored);
            setSaved(stored);
            notify(t('settings.credit.saved', 'Saved. New orders earn at the new rates from now; credit already issued keeps its value.'), { type: 'success' });
        } catch (e: any) {
            const message = e?.response?.status === 400
                ? t('settings.credit.invalid', 'One of the values is out of range')
                : t('common.saveFailed', 'Save failed');
            notify(message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    }, [form, notify, t]);

    const setNumber = (key: NumericKey, raw: string) => {
        if (!form) return;
        const value = raw === '' ? 0 : Number(raw);
        if (Number.isNaN(value)) return;
        setForm({ ...form, [key]: value });
    };

    return (
        <Card sx={{ mt: 2, maxWidth: 760 }}>
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <AccountBalanceWalletIcon sx={{ color: 'var(--color-primary)' }} />
                    <Typography variant="h6">{t('settings.credit.title', 'Vello Credit')}</Typography>
                </Stack>

                {loading || !form ? (
                    <Box sx={{ py: 3 }}><CircularProgress size={24} /></Box>
                ) : (
                    <>
                        {summary && (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {[
                                    ['owed', euro(summary.outstandingLiability)],
                                    ['issued', euro(summary.issuedThisMonth)],
                                    ['redeemed', euro(summary.redeemedThisMonth)],
                                    ['expired', euro(summary.expiredThisMonth)],
                                    ['commission', euro(summary.commissionThisMonth)],
                                    ['ratio', summary.issuedToCommissionPercent == null ? '—' : `${summary.issuedToCommissionPercent}%`],
                                    ['owedToRestaurants', euro(summary.owedToRestaurantsThisMonth)]
                                ].map(([key, value]) => (
                                    <Grid key={key} size={{ xs: 6, sm: 4 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t(`settings.credit.stats.${key}`)}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {summary && summary.restaurantPayoutsThisMonth?.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {t('settings.credit.payouts.title', 'Transfer to restaurants this month')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {t('settings.credit.payouts.help', 'Credit that covered part of a restaurant\'s payout. Saferpay paid them less by this amount; Vello owes it to them.')}
                                </Typography>
                                <Table size="small" sx={{ maxWidth: 520 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('settings.credit.payouts.restaurant', 'Restaurant')}</TableCell>
                                            <TableCell align="right">{t('settings.credit.payouts.orders', 'Orders')}</TableCell>
                                            <TableCell align="right">{t('settings.credit.payouts.amount', 'Amount')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summary.restaurantPayoutsThisMonth.map((row) => (
                                            <TableRow key={row.locationId}>
                                                <TableCell>{row.locationName}</TableCell>
                                                <TableCell align="right">{row.orders}</TableCell>
                                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{euro(row.amount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        <FormControlLabel
                            sx={{ mb: 2 }}
                            control={
                                <Switch
                                    checked={form.enabled}
                                    color="primary"
                                    onChange={(_: any, checked: boolean) => setForm({ ...form, enabled: checked })}
                                />
                            }
                            label={t('settings.credit.enabled', 'Customers earn and spend credit')}
                        />

                        <FormControlLabel
                            sx={{ mb: 2, display: 'block' }}
                            control={
                                <Switch
                                    checked={form.foodOnly}
                                    color="primary"
                                    onChange={(_: any, checked: boolean) => setForm({ ...form, foodOnly: checked })}
                                />
                            }
                            label={t('settings.credit.foodOnly', 'Credit pays for food only — delivery and service are always paid in money')}
                        />

                        <Grid container spacing={2}>
                            {FIELDS.map(({ key, unit, step }) => (
                                <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t(`settings.credit.fields.${key}.label`)}
                                        helperText={t(`settings.credit.fields.${key}.help`)}
                                        value={form[key]}
                                        onChange={(e) => setNumber(key, e.target.value)}
                                        slotProps={{
                                            htmlInput: { step, min: 0 },
                                            input: {
                                                startAdornment: unit === '€' ? <InputAdornment position="start">€</InputAdornment> : undefined,
                                                endAdornment: unit !== '€' ? <InputAdornment position="end">{unit}</InputAdornment> : undefined
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                disabled={saving || !dirty}
                                onClick={onSave}
                                label={saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                            />
                        </Box>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default CreditSettingsCard;
